import { buildFunnel, computeCostBar, costBarSentence, splitBudget, titleCase, type BudgetCeiling, type ClientProcessDto, type CostBarDto, type PhaseKey, type ProjectFunnelDto } from '@nsb/core';
import type { ComponentType, ReactElement } from 'react';
import { CostBar } from './CostBar.tsx';
import { homeSection, WAY_ROUND } from './homeWords.ts';
import { PriceFunnel } from './PriceFunnel.tsx';
import { clientTiles } from './tree/client.ts';
import type { TilePhase, TileStep } from './tree/model.ts';
import { TreeView } from './tree/TreeView.tsx';
import { WayRound } from './WayRound.tsx';
import { BudgetWaitingRow, WaitingOnYou } from '../screens/project/Waiting.tsx';
import { YoursRow } from '../screens/project/YoursRow.tsx';

/**
 * The Introduction's drawings (Jason, 2026-10-06: "real visual and real
 * simple ... it should be how a funnel could look in the real app"; "a couple
 * contrived steps that use actual icons"): each the real component the home
 * draws (`of`), fed a small made-up example through the same core functions a
 * project's data goes through, so the bands, marks, words and standings are
 * the portal's own and only the numbers and how the steps stand are the
 * example's. The steps are the five steps of the organization's tree Jason
 * named, as one chain, sent by the API with their painted pictures (Q1),
 * drawn as the client's own process is (R6). A help panel draws through here or not at all
 * (`test/introduction.test.tsx` holds each entry to its component).
 */

export const INTRODUCTION_PANELS = 5;

/** One panel's words: its title and one line under it, a dozen words at most (Jason: "no one reads anything"). */
export const INTRODUCTION_LINE_WORDS = 12;

/** The five panels' words, in order. */
export const INTRODUCTION_WORDS: readonly { title: string; line: string }[] = [
  { title: 'Your project, in one place', line: 'What it costs, where it is, and what we need from you.' },
  { title: homeSection('price'), line: 'What is billed, what you have paid, and your budget.' },
  { title: homeSection('funnel'), line: 'A range at first, one number by the end.' },
  { title: homeSection('tree'), line: 'Every step and where it stands: done, under way or not started.' },
  { title: 'Nothing is a surprise', line: 'Open the Introduction again from the ? beside the title.' },
];

/** What a drawing may need from whoever draws it: the day, and the example of the process the client view sends. */
export interface HelpContext {
  today: string;
  example: ClientProcessDto;
}

/** A small example: $40 billed and $30 paid, with a budget of $150 all in, 10% of it held inside it for contingency. */
export function exampleCostBar(): CostBarDto {
  const bar = computeCostBar({ billedCents: 40_00, moneyInCents: 30_00 });
  return {
    bar,
    sentence: costBarSentence(bar),
    ceiling: splitBudget(150_00, 10, 'inside'),
    pendingBudget: null,
    internal: false,
  };
}

/**
 * The five phases and the marks closing them, as the example funnel has drawn them since the Introduction was made:
 * the names and words the software gave them before the base tree (2026-10-07, when they left core). Whether the
 * example should be drawn from the organization's own tree's phases and closers instead waits on Jason's answer (L12).
 */
export const EXAMPLE_CLOSERS: readonly { phase: { key: PhaseKey; name: string; what: string }; key: string; name: string }[] = [
  { phase: { key: 'discovery', name: 'Discovery', what: 'The first conversation, the site and what it could cost. No commitment yet.' }, key: 'deposit', name: 'Deposit' },
  { phase: { key: 'design', name: 'Design', what: 'Concept, design and renders, reviewed together iteration by iteration, and the first estimate.' }, key: 'design_lock', name: 'Design lock' },
  { phase: { key: 'development', name: 'Pre-construction', what: 'Engineering, the drawings for the build, permits and the build contract.' }, key: 'build_authorized', name: 'Construction authorized' },
  { phase: { key: 'construction', name: 'Construction', what: 'The build itself: the site, the foundation, the frame, the roof, the systems and the finishes.' }, key: 'substantial_completion', name: 'Move-in ready' },
  { phase: { key: 'closeout', name: 'Close out', what: 'The punch list, the walkthrough, the last bill and handover.' }, key: 'final', name: 'Final' },
];
const CLOSERS = EXAMPLE_CLOSERS;

/**
 * A funnel as one could look on a real job, every phase dated and bracketed:
 * a $100k–$200k budget at the start, two phases passed, the next three
 * planned, and what has been billed so far.
 */
export function exampleFunnel(today: string): ProjectFunnelDto {
  const d = (months: number) => {
    const x = new Date(`${today}T12:00:00Z`);
    x.setUTCMonth(x.getUTCMonth() + months);
    return x.toISOString().slice(0, 10);
  };
  const budget = { lowCents: 100_000_00, highCents: 200_000_00 };
  const dates = [d(-5), d(-2), d(1), d(7), d(9)];
  const funnel = buildFunnel({
    today,
    startedOn: d(-7),
    endedOn: null,
    budget,
    gates: CLOSERS.map((c, i) => ({
      key: c.key,
      name: titleCase(c.name),
      note: null,
      passedOn: dates[i]! < today ? dates[i]! : null,
      plans: [{ on: dates[i]!, cause: 'baseline' as const, reason: null }],
      mark: 'gate' as const,
      sureAfter: [0.6, 0.3, 0.15, 0.05, 0][i],
    })),
    phases: CLOSERS.map((c, i) => ({ key: c.phase.key, name: c.phase.name, what: c.phase.what, sureAfter: [0.6, 0.3, 0.15, 0.05, 0][i]!, end: { key: c.key, name: titleCase(c.name), passedOn: dates[i]! < today ? dates[i]! : null, plans: [] } })),
    spent: [
      { on: d(-4), amountCents: 15_000_00 },
      { on: d(-1), amountCents: 35_000_00 },
    ],
  });
  // The drawing alone, as asked: no list of the phases under it.
  return { ...funnel, phases: [], budget, ceiling: null, seasons: { across: [], down: [] }, seasonsPainting: false };
}

/**
 * The process as a client's own is drawn (TreeView, through the client's tiles): the five steps of the organization's
 * tree Jason named, as one chain (`ClientViewDto.example`, domain/introduction.ts `EXAMPLE_KEYS`), with their pictures.
 * Drawn only: a step is not opened here, where Esc closes the Introduction.
 */
export function exampleTree(example: ClientProcessDto): { phases: TilePhase[]; steps: TileStep[] } {
  const { phases, steps } = clientTiles(example);
  return { phases, steps };
}

/** The way round, every place of it as the home shows it before anything fills it: the project's places, in one place. */
export const exampleWayRound = () => WAY_ROUND.map((w) => ({ path: w.path, label: w.label }));

/**
 * What waits on the client, before it is due (Jason picked it for "Nothing is a surprise", 2026-10-07): the example's
 * budget to confirm, and the step of theirs the example has to approve, with the note the tree writes for them.
 */
export function exampleWaiting(c: HelpContext): { budget: BudgetCeiling | null; step: ClientProcessDto['steps'][number] | null } {
  const step = c.example.steps.find((s) => s.kind === 'gate' && s.owner === 'you' && s.key === 'design_lock') ?? c.example.steps.find((s) => s.kind === 'gate' && s.owner === 'you') ?? null;
  return { budget: exampleCostBar().ceiling, step };
}

export const HELP_DRAWINGS: Record<'place' | 'price' | 'funnel' | 'process' | 'waiting', { of: ComponentType<any>; draw: (c: HelpContext) => ReactElement }> = {
  place: { of: WayRound, draw: () => <WayRound items={exampleWayRound()} /> },
  price: { of: CostBar, draw: () => <CostBar data={exampleCostBar()} size="client" reader="client" /> },
  funnel: { of: PriceFunnel, draw: (c) => <PriceFunnel funnel={exampleFunnel(c.today)} /> },
  process: { of: TreeView, draw: (c) => <TreeView {...exampleTree(c.example)} label="A few steps, as an example" /> },
  waiting: {
    of: WaitingOnYou,
    draw: (c) => {
      const { budget, step } = exampleWaiting(c);
      return (
        <WaitingOnYou className="max-w-[23rem]">
          <ul className="m-0 list-none divide-y divide-rule border-b border-rule p-0">
            {budget ? <BudgetWaitingRow split={budget} by="NSB" /> : null}
            {step ? <YoursRow projectId="" step={{ id: step.id, title: step.title, kind: 'gate', why: step.why }} preview={false} drawn /> : null}
          </ul>
        </WaitingOnYou>
      );
    },
  },
};
